import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/api';

const PRIORITY_TO_SEVERITY: Record<number, string> = { 0: 'critical', 1: 'high', 2: 'medium', 3: 'low' };

interface AlertDetail {
  id: number;
  priority: number;
  title: string;
  description: string;
  status: string;
}

export default function AlertEditPage() {
  const { alertId } = useParams<{ alertId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [alertStatus, setAlertStatus] = useState('open');
  const [severity, setSeverity] = useState('low');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const fetchAlert = useCallback(async () => {
    try {
      const res = await api.get(`/monitors/alerts/${alertId}`);
      const a: AlertDetail = res.data;
      setAlertStatus(a.status || 'open');
      setSeverity(PRIORITY_TO_SEVERITY[a.priority] || 'low');
      setTitle(a.title);
      setDescription(a.description);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load alert');
    } finally {
      setLoading(false);
    }
  }, [alertId]);

  useEffect(() => { fetchAlert(); }, [fetchAlert]);

  // Auto-dismiss success
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => navigate(`/alerts/${alertId}`), 1500);
      return () => clearTimeout(t);
    }
  }, [success, navigate, alertId]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/alerts/${alertId}`, {
        status: alertStatus,
        severity,
        title,
        description,
      });
      setSuccess('Alert updated successfully');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update alert');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate(`/alerts/${alertId}`)} style={styles.backBtn}>
          &larr; Back to Alert
        </button>

        <h2 style={styles.sectionTitle}>Edit Alert</h2>

        {loading && <p style={styles.statusText}>Loading...</p>}

        {!loading && (
          <div style={styles.card}>
            {error && <div style={styles.errorBanner}>{error}</div>}
            {success && <div style={styles.successBanner}>{success}</div>}

            <div style={styles.field}>
              <label style={styles.label}>Status</label>
              <select value={alertStatus} onChange={e => setAlertStatus(e.target.value)} style={styles.select}>
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Severity</label>
              <select value={severity} onChange={e => setSeverity(e.target.value)} style={styles.select}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Description</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={6}
                style={styles.textarea}
              />
            </div>

            <div style={styles.buttonRow}>
              <button onClick={() => navigate(`/alerts/${alertId}`)} style={styles.cancelBtn}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} style={{ ...styles.saveBtn, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: 'var(--bg-base)' },
  main: { maxWidth: 640, margin: '0 auto', padding: 'var(--space-8) var(--space-6)' },
  backBtn: {
    background: 'none', border: 'none', color: 'var(--accent-text)', fontSize: 'var(--text-md)',
    fontWeight: 500, fontFamily: 'var(--font-body)', cursor: 'pointer', padding: 0, marginBottom: 'var(--space-5)',
  },
  sectionTitle: { fontSize: 'var(--text-xl)', fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--text-primary)', marginBottom: 'var(--space-5)' },
  statusText: { color: 'var(--text-secondary)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)' },
  card: { background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-subtle)', padding: '28px' },
  errorBanner: {
    background: 'var(--status-critical-muted)', color: 'var(--status-critical)', padding: '12px 16px', borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)', border: '1px solid rgba(255,71,87,0.3)', marginBottom: 'var(--space-5)',
  },
  successBanner: {
    background: 'var(--status-success-muted)', color: 'var(--status-success)', padding: '12px 16px', borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)', border: '1px solid rgba(23,178,106,0.3)', marginBottom: 'var(--space-5)',
  },
  field: { marginBottom: 'var(--space-5)' },
  label: {
    display: 'block', fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', marginBottom: 6,
  },
  select: {
    width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)',
    background: 'var(--input-bg)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', color: 'var(--text-primary)',
    boxSizing: 'border-box' as const, colorScheme: 'dark',
  },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', color: 'var(--text-primary)', background: 'var(--input-bg)',
    boxSizing: 'border-box' as const, colorScheme: 'dark',
  },
  textarea: {
    width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', color: 'var(--text-primary)', background: 'var(--input-bg)',
    resize: 'vertical' as const, boxSizing: 'border-box' as const, colorScheme: 'dark',
  },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 },
  cancelBtn: {
    padding: '10px 24px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)', background: 'var(--bg-surface-raised)',
    fontSize: 'var(--text-md)', fontWeight: 500, fontFamily: 'var(--font-body)', color: 'var(--text-primary)', cursor: 'pointer',
  },
  saveBtn: {
    padding: '10px 24px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)',
    color: '#fff', fontSize: 'var(--text-md)', fontWeight: 600, fontFamily: 'var(--font-body)', cursor: 'pointer',
  },
};
